import searchService from 'backend/services/searchService'
import { SearchParams } from 'backend/services/types/urApiService.types'
import HorizontalFilterContainer from 'components/HorizontalFilterContainer/HorizontalFilterContainer'
import Layout from 'components/Layout/Layout'
import OgMetadata from 'components/OgMetadata/OgMetadata'
import { DataLayerPageType, DefaultOptions, FilterType } from 'frontend/enums'
import { CategoryItem, SearchResult } from 'frontend/types'
import usePageTracking from 'frontend/utils/hooks/usePageTracking'
import { getOrigin, searchParams } from 'frontend/utils/urlHelpers'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import { getCategoriesForMenu } from 'utils/categoryUtil'
import { queryParamTransformer } from 'utils/transformers'

interface Props {
  initialSearchResult: SearchResult
  initialSearchParams: SearchParams
  highlightedCategories: CategoryItem[]
  minorityLanguageCategories: CategoryItem[]
  origin: string
}

export const SearchPage = ({
  highlightedCategories,
  minorityLanguageCategories,
  initialSearchResult,
  initialSearchParams,
  origin,
}: Props) => {
  usePageTracking(DataLayerPageType.SEARCH_PAGE)
  const { header, query } = searchParams(initialSearchParams)
  let pageTitle = ''
  const noQuery = !query || query.length === 0

  if (header) {
    pageTitle = header
  } else if (noQuery) {
    pageTitle = 'Alla program och serier'
  }

  return (
    <>
      <Head>
        <title>Söksida - UR Play</title>
        <meta
          name="description"
          content="UR Play är en streamingtjänst från UR som erbjuder underhållande och lärorika barnprogram, poddar, dokumentärer, kortfilmer och föreläsningar."
        />
        <link rel="canonical" href={`${origin}/sok`} />
        <meta name="robots" content="noindex" />
      </Head>
      <OgMetadata
        title="UR Play | Dina favoritprogram när och var du vill"
        description="UR Play är en streamingtjänst från UR som erbjuder underhållande och lärorika barnprogram, poddar, dokumentärer, kortfilmer och föreläsningar."
        sharePath={'/sok'}
      />
      <Layout
        categories={highlightedCategories}
        minorityLanguageCategories={minorityLanguageCategories}
      >
        <HorizontalFilterContainer
          filterHeader={{
            title: pageTitle,
            query: query,
            headingLevel: 'h1',
          }}
          initialSearchResult={initialSearchResult}
          initialSearchParams={initialSearchParams}
          includeRelevance
          blockTracking={{
            blockIndex: 1,
            blockType: 'carpet',
            blockTitle: 'söksidan',
          }}
          hiddenFilters={[
            FilterType.SCHOOL_SUBJECT,
            FilterType.KNOWLEDGE_AREA,
            FilterType.SORT,
          ]}
        />
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ctx => {
  const { query } = ctx

  const { highlightedCategories, minorityLanguageCategories } =
    await getCategoriesForMenu()
  const origin = getOrigin(ctx)

  const initialSearchParams: SearchParams = {
    sort: DefaultOptions.EMPTY,
    ...queryParamTransformer(query, 'bracket'),
  }

  const initialSearchResult = await searchService.search(
    initialSearchParams,
    'extended',
  )

  return {
    props: {
      initialSearchResult,
      initialSearchParams,
      highlightedCategories,
      minorityLanguageCategories,
      origin,
    },
  }
}

export default SearchPage
